// Sentry must initialize before any other code runs — this file is imported
// as the very first line of main.tsx.
import * as Sentry from "@sentry/react";
import { isTrackingEnabled } from "@/lib/tracking-toggles";
import { matchesChunkLoadPattern } from "@/lib/chunkLoadError";
import {
  INJECTED_SCRIPT_MESSAGE_PATTERNS,
  isThirdPartyOriginStack,
} from "@/lib/thirdPartyOrigin";

const dsn = import.meta.env.VITE_SENTRY_DSN;
const isProd = import.meta.env.MODE === "production";

// Admin /admin/tracking toggle — flipping off + reload prevents Sentry
// from initializing at all this session (clean Lighthouse-comparison
// workflow). localStorage is sync, available before React mounts.
//
// 2026-05-10 audit pass: Sentry.init() does ~25-40ms of work on cold start
// (window.onerror hook, console.error override, navigation perf
// instrumentation, periodic flush scheduling). Deferring it to idle moves
// that work off the TTI critical path. Tradeoff: window.onerror isn't wired
// during the first ~100-200ms — extremely-early render errors aren't caught
// by Sentry. ErrorBoundary still catches React render errors regardless.
function initSentry(): void {
  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_APP_VERSION,
    sendDefaultPii: true,
    integrations: [
      Sentry.browserTracingIntegration(),
      // Default Sentry integrations capture window.onerror and
      // unhandledrejection, but NOT console.error. The homegrown
      // client_errors logger does wrap console.error — without this,
      // Sentry sees a strict subset of what client_errors sees. Capture
      // console.error so the two dashboards align.
      Sentry.captureConsoleIntegration({ levels: ["error"] }),
    ],
    tracesSampleRate: isProd ? 0.1 : 1.0,
    // Session/error replay disabled in prod 2026-05-04 — rrweb shipped
    // ~150KB gzipped in framework-vendor and was the dominant
    // unused-javascript opportunity in Lighthouse audits across all
    // routes (~7.5s aggregate savings). Tradeoff: lose visual session
    // replay when debugging user-reported errors. Errors still reach
    // Sentry via window.onerror + unhandledrejection + the
    // captureConsoleIntegration above. To re-enable for a specific
    // debugging window:
    //   integrations: [Sentry.replayIntegration({ block: ["iframe"] })]
    // and bump replaysOnErrorSampleRate / replaysSessionSampleRate.
    beforeSend(event, hint) {
      // Filter known-noise patterns. Mirrors filtering in src/lib/errorLogger.ts
      // so we don't burn the Sentry quota on browser-extension / stale-chunk
      // / third-party-script noise. (2026-05-03: ported after CSP unblock
      // surfaced a flood of stale-chunk errors from post-deploy tabs.)
      const exception = hint?.originalException;
      const message = (
        event.message ||
        (exception instanceof Error ? exception.message : String(exception ?? ""))
      ).toLowerCase();

      // Stale chunk after a Vercel deploy — user's tab has the old chunk
      // hash. Reload fixes it; logging it adds nothing actionable, and
      // ErrorBoundary already handles the recovery.
      //
      // 2026-08-02 (S16-AUD-127): the patterns moved to
      // @/lib/chunkLoadError. This block and errorLogger's
      // shouldSkipClientError now read the same literal, which is what
      // design-rules.md §12.6 asks for ("they must agree"). Previously the
      // two lists were hand-mirrored and this one also had a second,
      // separate chunk block further down that the other file did not.
      if (matchesChunkLoadPattern(message)) {
        return null;
      }

      // 2026-05-19 (SPOTIVIBLY-WEB-18): Supabase auth rate-limit responses
      // logged via console.error and captured by captureConsoleIntegration.
      // Code `over_request_rate_limit` / message "Request rate limit reached"
      // / status 429 is Supabase legitimately throttling a user hammering
      // sign-in or sign-up endpoints. Not an actionable bug — handled by
      // the calling code (mechanism: auto.core.capture_console, handled=yes).
      // If this class ever spikes, client_errors will surface the trend;
      // single events don't need Sentry alerts.
      if (
        message.includes("request rate limit reached") ||
        message.includes("over_request_rate_limit")
      ) {
        return null;
      }

      // Our own error-logger infrastructure meta-errors. These fire when the
      // logger itself failed to insert into client_errors (usually the same
      // network drop that caused the original error). Belt-and-braces with
      // errorLogger.ts's `_rawConsoleError` — even if a [ErrorLogger] message
      // reaches Sentry via some path we haven't covered, we drop it here.
      // SPOTIVIBLY-WEB-V, 2026-05-11.
      if (message.startsWith("[errorlogger]")) {
        return null;
      }

      // Browser extension / third-party noise.
      if (
        message.includes("extension context invalidated") ||
        message.includes("the message port closed before a response was received") ||
        message.includes("failed to execute 'importscripts'") ||
        message.includes(".wasm.wasm") ||
        message.includes("object not found matching id") ||
        // 2026-05-21: a browser extension on the user's page is calling
        // Sentry.init() in extension context. Sentry's own SDK detects
        // this and warns via console.error; captureConsoleIntegration
        // picks it up as if it were ours. Not actionable — the call is
        // from THEIR extension, not our app. Observed on both
        // / (with OAuth #access_token hash, authed user) and on
        // /spotify-playlist-creator (anon).
        message.includes("you cannot use sentry.init() in a browser extension") ||
        // 2026-06-28 (Sentry batch SPOTIVIBLY-WEB-1C/1K + client-errors): more
        // browser-extension / injected-script noise. None of these symbols
        // exist anywhere in our source (grep-verified). runtime.sendMessage is
        // a WebExtension API; the unsafe-eval EvalError is our CSP (vercel.json
        // ships no 'unsafe-eval') correctly blocking an injected script;
        // standardSelectors + gmo are extension-internal globals.
        message.includes("invalid call to runtime.sendmessage") ||
        message.includes("refused to evaluate a string as javascript") ||
        message.includes("standardselectors") ||
        message.includes("can't find variable: gmo") ||
        // 2026-08-19: injected-script collisions ("Identifier 'initCoreHelpers'
        // has already been declared"). Stackless, so the origin check below
        // cannot see them. Shared literal with errorLogger.ts.
        INJECTED_SCRIPT_MESSAGE_PATTERNS.some((p) => message.includes(p)) ||
        // A browser layout notice, not an error — a ResizeObserver callback
        // resized its own observed element and the browser deferred a frame.
        message.includes("resizeobserver loop")
      ) {
        return null;
      }

      // 2026-08-19: structural sibling of the block above. Drops an event whose
      // THROWING frame belongs to an extension content script or a rewriting
      // proxy mirror, whatever it says. Reads the raw stack string rather than
      // event.exception frames on purpose: Sentry orders frames oldest-first,
      // so "the frame that threw" is the LAST one there and the first one here,
      // and one shared text parser cannot get that backwards.
      // Mirrors errorLogger.ts's shouldSkipClientError (design-rules §12.6).
      if (
        exception instanceof Error &&
        isThirdPartyOriginStack(exception.stack)
      ) {
        return null;
      }

      // "Failed to fetch" — almost always a transient network blip on the
      // user's end (cellular dropout, captive portal, browser closed mid-
      // request). Two cases handled:
      //   1. Stackless: classic browser-level network error with no JS
      //      origin. Always noise.
      //   2. Captured via console.error (mechanism.type === 'console'): the
      //      app already caught + logged the error; Sentry just slurped it
      //      from console via captureConsoleIntegration. By definition
      //      handled — not an actionable Sentry signal. (2026-05-07: dropping
      //      this class after a /playlists Supabase fetch flake produced
      //      a 'TypeError: Failed to fetch' alert with stack but
      //      mechanism=auto.core.capture_console + handled=yes.)
      //
      // 2026-05-07 follow-up: the original filter only matched messages
      // with the "typeerror: " prefix, but Supabase's auth-js logs the
      // bare exception via console.error("Failed to fetch (host)") —
      // event.message arrives without the TypeError prefix and PR #211's
      // filter missed it. Issue SPOTIVIBLY-WEB-K (07:24Z, 10h after the
      // PR #211 deploy) re-fired the same noise.
      //
      // 2026-05-07 audit follow-up: tightened the host-suffixed match
      // to ONLY drop known third-party hosts (Supabase, Google APIs,
      // Stripe, etc.). Previously `failed to fetch (` matched ANY
      // suffix including our own edge functions — we'd silently lose
      // signal if a real save/generation network call failed. Now real
      // app failures still report; only the known-noisy third parties
      // get filtered.
      const hasStack = !!event.exception?.values?.[0]?.stacktrace?.frames?.length;
      // 2026-05-08 follow-up: iOS Safari emits "Load failed" instead of
      // "Failed to fetch" for the same network-blip class (see Sentry
      // SPOTIVIBLY-WEB-P — 2 events, 2 users on /generate, all from
      // Supabase fetches that dropped mid-flight). The two messages are
      // equivalent in cause and severity, so the filter must catch both.
      // Pattern: optional "typeerror: " prefix, then "failed to fetch"
      // OR "load failed", then a host suffix in the noisy-host list.
      // 2026-07-15 (SPOTIVIBLY-WEB-1V): two changes, both keeping the strict
      // host allowlist that the 2026-05-07 audit added.
      //
      //   1. Allow an app-context PREFIX. The pattern was anchored at ^, so it
      //      only matched a bare blip. But our own code catches the identical
      //      blip and re-logs it with context:
      //        "[Generate] auto-save failed after 3 attempts: Database save
      //         failed: TypeError: Failed to fetch (tnffhgpavnrjozjjiasz.supabase.co)"
      //      Same user-side network drop, same non-actionability — it escaped
      //      the filter purely because it was wrapped. `(^|: )` keeps the match
      //      tight: the blip must start the message or follow a ": " separator,
      //      so a host merely MENTIONED mid-sentence won't match. The $ anchor
      //      + host allowlist still mean the blip must be the TERMINAL cause.
      //      Verified on the WEB-1V event: the client's 3 save attempts failed
      //      on a network drop, the server-side auto-retry cron healed it
      //      (generation_attempts.status='autoretry-saved') and the playlist
      //      landed 8 min later. Nothing for us to fix.
      //      Real save bugs (RLS denial, 23505, 'permission denied for table',
      //      'No playlist ID returned') carry no host suffix — they do NOT
      //      match here and still alert. That's the S16-042 signal, preserved.
      //
      //   2. Add Firefox's "NetworkError when attempting to fetch resource."
      //      The bare form was already dropped by the equality checks below,
      //      but the host-suffixed form wasn't — it leaked into client_errors
      //      on 2026-07-14 19:52Z. Same class as Chrome's "Failed to fetch"
      //      and iOS Safari's "Load failed".
      //
      // 2026-07-18: added images.unsplash.com — a "Load failed
      // (images.unsplash.com)" unhandled_rejection from /my-vibe (the vibe
      // quiz renders decorative Unsplash images; the rejecting fetch came
      // from an extension userscript wrapping window.fetch, not our code).
      // Same non-actionable third-party blip class as the hosts above.
      //
      // Mirrored byte-identically in src/lib/errorLogger.ts — the parity test
      // at src/lib/__tests__/noisy-hosts-regex-parity.test.ts enforces it.
      const NOISY_THIRD_PARTY_HOSTS_RE =
        // 2026-07-22: added googlesyndication.com + doubleclick.net (Google
        // Ads pixels blocked by ad blockers / privacy extensions). MUST stay
        // identical to the copy in src/lib/errorLogger.ts — design-rules §12.6.
        /(^|: )(typeerror: )?(failed to fetch|load failed|networkerror when attempting to fetch resource\.) \((tnffhgpavnrjozjjiasz\.supabase\.co|.*\.googleapis\.com|.*\.stripe\.com|js\.stripe\.com|.*\.youtube\.com|.*\.google-analytics\.com|.*\.googletagmanager\.com|.*\.googlesyndication\.com|.*\.doubleclick\.net|.*\.hotjar\.com|.*\.sentry\.io|images\.unsplash\.com|.*\.scdn\.co|.*\.spotifycdn\.com)\)$/;
      // A BARE failure message carries no evidence about whose request it was,
      // so it keeps the stack gate below: with a stack it might be our own
      // save or generation call failing, which we want to hear about.
      const isBareFailedFetch =
        message === "typeerror: failed to fetch" ||
        message === "failed to fetch" ||
        // iOS Safari bare-message variant (no host suffix). Matches the
        // two-event pattern logged from /generate on 2026-05-07.
        message === "typeerror: load failed" ||
        message === "load failed" ||
        message === "networkerror when attempting to fetch resource.";
      // A HOST-SUFFIXED failure names a third party we do not control, and the
      // host is the evidence. Gating it on the stack meant a known-noisy blip
      // still paged us whenever Sentry managed to attach frames — which is how
      // "TypeError: Load failed (i.scdn.co)" (Spotify's art CDN, on a user's
      // flaky mobile network) opened a production issue on 2026-08-03 with a
      // vendor-bundle stack. errorLogger.ts already applies this regex with no
      // gate at all, and its 2026-05-11 note records dropping the equivalent
      // error_type gate for the same reason; this brings Sentry in line.
      // design-rules §12.6: host-suffix patterns are trustworthy regardless.
      const isKnownThirdPartyHostFailure = NOISY_THIRD_PARTY_HOSTS_RE.test(message);
      const mechanismType = event.exception?.values?.[0]?.mechanism?.type;
      const fromConsole = mechanismType === "console" || mechanismType === "auto.core.capture_console";

      if (isKnownThirdPartyHostFailure) {
        return null;
      }
      if (isBareFailedFetch && (!hasStack || fromConsole)) {
        return null;
      }

      // 2026-05-13: mirror errorLogger filters so Sentry doesn't get the
      // same noise we already filter from client_errors.
      // 'invalid data: key name: event' — Microsoft Clarity IDB rejection.
      // We don't use IDB ourselves; any hit is a third-party tracker.
      // (The stale-chunk half of this block folded into the
      // matchesChunkLoadPattern check above on 2026-08-02.)
      if (message.includes("invalid data: key name: event")) {
        return null;
      }

      // 2026-05-26: injected third-party pixel scripts (Meta / UET / Hotjar /
      // Clarity / LinkedIn / TikTok / Reddit / X — loaded via
      // ManagedScripts.tsx) call their own fetch + JSON.parse on config
      // endpoints. When Cloudflare / pixel-vendor returns an HTML
      // interstitial (5xx, captcha, rate-limit page) the parse throws
      // SyntaxError as an unhandled promise rejection from the
      // dynamically-evaluated VM module. Pattern is "Unexpected token '<'"
      // or "Unexpected token 'h', '<html..." or "...is not valid JSON".
      // Our own JSON.parse calls are all wrapped in try/catch (verified
      // 2026-05-26) so any "<html"-shaped parse leak is third-party.
      if (
        (message.includes("syntaxerror") && message.includes("is not valid json")) ||
        message.includes("unexpected token '<'") ||
        message.includes('unexpected token "<"') ||
        message.includes("unexpected token 'h', \"<html") ||
        message.includes("unexpected token 'h', \" html><html")
      ) {
        return null;
      }

      return event;
    },
  });
}

// 2026-06-28: gate Sentry init off in local `vite dev` (import.meta.env.DEV).
// Staging + prod are vite builds (DEV=false) and still report. This removes
// the "[vite] Failed to reload /src/pages/staging/*.tsx" HMR-error class
// (SPOTIVIBLY-WEB-1F/1J) that can only fire under the dev server.
if (dsn && !import.meta.env.DEV && isTrackingEnabled("sentry")) {
  // requestIdleCallback runs Sentry init when the main thread is otherwise
  // idle — typically right after first paint. setTimeout fallback for
  // Safari (which still doesn't ship requestIdleCallback as of WebKit 18).
  // 100ms is enough headroom for the React tree to mount and paint.
  if (typeof window !== "undefined" && "requestIdleCallback" in window) {
    (window as Window & { requestIdleCallback: (cb: () => void, opts?: { timeout: number }) => void })
      .requestIdleCallback(initSentry, { timeout: 2000 });
  } else {
    setTimeout(initSentry, 100);
  }
}
